import React, { useRef } from 'react';
import classnames from 'classnames';

import { Svg } from 'src/components/Svg/Svg';
import { SvgIconNames } from 'src/components/Svg/icons-types';

export type TabItem = {
    content: React.ReactNode;
    icon?: string;
    id: string;
    title: string;
};

export type DesktopTabsProps = {
    borderStyles?: React.CSSProperties;
    isPageBuilder?: boolean;
    layoutStyles?: React.CSSProperties;
    tabs: TabItem[];
    openIndexes: number[];
    handleTabClick: (index: number) => void;
};

export function DesktopTabs(props: DesktopTabsProps) {
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([]); // Store references to tab buttons

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        const { key } = event;
        const totalTabs = props.tabs.length;

        if (key === 'ArrowRight') {
            const nextIndex = (index + 1) % totalTabs;
            tabRefs.current[nextIndex]?.focus();
        } else if (key === 'ArrowLeft') {
            const prevIndex = (index - 1 + totalTabs) % totalTabs;
            tabRefs.current[prevIndex]?.focus();
        } else if (key === 'Home') {
            tabRefs.current[0]?.focus();
        } else if (key === 'End') {
            tabRefs.current[totalTabs - 1]?.focus();
        }
    };

    return (
        <>
            <div className="tabs" role="tablist">
                {props.tabs.map((item, index) => {
                    const active = props.openIndexes.includes(index);
                    return (
                        <button
                            key={`tab-${item.id}`}
                            type="button"
                            className="tabs__item"
                            data-ui-active={active}
                            role="tab"
                            id={`${item.id}-tab`}
                            aria-controls={`${item.id}-panel`}
                            aria-selected={active}
                            tabIndex={active ? 0 : -1}
                            onClick={() => props.handleTabClick(index)}
                            onKeyDown={(event) => handleKeyDown(event, index)}
                            ref={(el) => (tabRefs.current[index] = el)}
                            style={props.borderStyles}
                        >
                            <span className="tabs__title">
                                {item.icon && <Svg name={item.icon as SvgIconNames} />} {item.title}
                            </span>
                            <Svg name="arrow-down" />
                        </button>
                    );
                })}
            </div>

            {props.tabs.map((item, index) => {
                const active = props.openIndexes.includes(index);
                return (
                    <div
                        key={`panel-${item.id}`}
                        style={props.borderStyles}
                        className={classnames('tabs__body', { tabs__body__pagebuilder: props.isPageBuilder })}
                        data-ui-active={active}
                        role="tabpanel"
                        aria-labelledby={`${item.id}-tab`}
                        id={`${item.id}-panel`}
                        hidden={!active}
                    >
                        {item.content}
                    </div>
                );
            })}
        </>
    );
}
